import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

const Teacher = () => {
  const [activeTab, setActiveTab] = useState('syllabus');
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [url, setUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [questionType, setQuestionType] = useState('multiple_choice');
  const [questionText, setQuestionText] = useState('');
  const [options, setOptions] = useState(['', '', '', '']);
  const [answer, setAnswer] = useState('');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [syllabi, setSyllabi] = useState([]);
  const [selectedSyllabus, setSelectedSyllabus] = useState(null);
  const [topics, setTopics] = useState([]);
  const [homework, setHomework] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [newSyllabusTitle, setNewSyllabusTitle] = useState('');
  const [newSyllabusDesc, setNewSyllabusDesc] = useState('');
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [newTopicDesc, setNewTopicDesc] = useState('');
  const [newHomeworkTitle, setNewHomeworkTitle] = useState('');
  const [editingCourse, setEditingCourse] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');

  useEffect(() => {
    fetchVideos();
    fetchCourses();
    fetchHomework();
  }, []);

  const fetchVideos = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/videos}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos', error);
    }
  };

  const handlePreviewVideo = () => {
    if (url) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      if (videoId) {
        setPreviewUrl(`https://www.youtube.com/embed/${videoId}`);
        setVideoTitle(`Video: ${videoId}`);
      }
    }
  };

  const loadQuestionTemplate = (template) => {
    if (template === 'mcq4') {
      setQuestionType('multiple_choice');
      setOptions(['Option A', 'Option B', 'Option C', 'Option D']);
      setAnswer('Option A');
    } else if (template === 'short') {
      setQuestionType('short_answer');
      setOptions([]);
      setAnswer('Sample answer');
    }
  };

  const fetchAnalytics = async (videoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_BASE_URL}/api/analytics?video_id=${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
      setShowAnalytics(true);
    } catch (error) {
      console.error('Error fetching analytics', error);
    }
  };

  const setAnalytics = (data) => {
    // Placeholder for analytics state setter
  };

  const handleAddVideo = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${config.API_BASE_URL}/api/videos}`, { url, course_id: selectedVideo }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUrl('');
      fetchVideos();
    } catch (error) {
      console.error('Error adding video', error);
    }
  };

  const handleAddQuestion = async () => {
    const token = localStorage.getItem('token');
    const newQuestion = {
      video_id: selectedVideo?.id, // Assume selected video
      type: questionType,
      question: questionText,
      options: questionType === 'multiple_choice' ? options : null,
      answer
    };
    try {
      await axios.post(`${config.API_BASE_URL}/api/questions}`, newQuestion, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestionText('');
      setOptions(['', '', '', '']);
      setAnswer('');
      alert('Question added');
    } catch (error) {
      alert('Error adding question');
    }
  };

  const fetchCourses = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/courses}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyllabi(response.data);
    } catch (error) {
      console.error('Error fetching courses', error);
    }
  };

  const fetchHomework = async () => {
    // Mock homework data for now
    setHomework([
      { id: 1, title: 'Algebra Practice Set', description: 'Complete exercises 1-20 from chapter 2', due_date: '2025-01-15', status: 'active' },
      { id: 2, title: 'Physics Lab Report', description: 'Write a report on the pendulum experiment', due_date: '2025-01-20', status: 'active' },
      { id: 3, title: 'History Essay', description: 'Write about the impact of ancient civilizations', due_date: '2025-01-25', status: 'active' }
    ]);
  };

  const handleCreateSyllabus = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${config.API_BASE_URL}/api/courses}`, {
        title: newSyllabusTitle,
        description: newSyllabusDesc
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewSyllabusTitle('');
      setNewSyllabusDesc('');
      fetchCourses();
      alert('Course created');
    } catch (error) {
      alert('Error creating course');
    }
  };

  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setEditTitle(course.title);
    setEditDescription(course.description);
  };

  const handleUpdateCourse = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${config.API_BASE_URL}/api/courses/${editingCourse.id}`, {
        title: editTitle,
        description: editDescription
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingCourse(null);
      fetchCourses();
      alert('Course updated successfully');
    } catch (error) {
      alert('Error updating course');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (window.confirm('Are you sure you want to delete this course? This will also delete all associated videos, homework, and student enrollments.')) {
      const token = localStorage.getItem('token');
      try {
        await axios.delete(`${config.API_BASE_URL}/api/courses/${courseId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchCourses();
        alert('Course deleted successfully');
      } catch (error) {
        alert('Error deleting course');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    window.location.href = '/';
  };

  return (
    <div className="container-fluid">
      {/* Dashboard Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow">
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1">👨‍🏫 Teacher Dashboard</h2>
                <p className="text-muted mb-0">Manage your courses, content, and student progress</p>
              </div>
              <button className="btn btn-outline-danger" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt me-2"></i>Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card text-center shadow">
            <div className="card-body">
              <div className="stats-card">
                <h3>{videos.length}</h3>
                <p className="mb-0">Total Videos</p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center shadow">
            <div className="card-body">
              <div className="stats-card">
                <h3>{syllabi.length}</h3>
                <p className="mb-0">Active Courses</p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center shadow">
            <div className="card-body">
              <div className="stats-card">
                <h3>{homework.length}</h3>
                <p className="mb-0">Assignments</p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center shadow">
            <div className="card-body">
              <div className="stats-card">
                <h3>85%</h3>
                <p className="mb-0">Avg. Completion</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow">
            <div className="card-header">
              <ul className="nav nav-tabs card-header-tabs" id="teacherTabs" role="tablist">
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${activeTab === 'syllabus' ? 'active' : ''}`} onClick={() => setActiveTab('syllabus')}>
                    <i className="fas fa-book me-2"></i>Course
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${activeTab === 'videos' ? 'active' : ''}`} onClick={() => setActiveTab('videos')}>
                    <i className="fas fa-video me-2"></i>Videos
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${activeTab === 'homework' ? 'active' : ''}`} onClick={() => setActiveTab('homework')}>
                    <i className="fas fa-tasks me-2"></i>Homework
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveTab('analytics')}>
                    <i className="fas fa-chart-bar me-2"></i>Analytics
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body">
          <div className="tab-content mt-4">
            {activeTab === 'syllabus' && (
              <div className="card">
                <div className="card-header">Course Management</div>
                <div className="card-body">
                  <div className="mb-3">
                    <input type="text" className="form-control mb-2" placeholder="New Course Title" value={newSyllabusTitle} onChange={(e) => setNewSyllabusTitle(e.target.value)} />
                    <textarea className="form-control mb-2" placeholder="Course Description" rows="3" value={newSyllabusDesc} onChange={(e) => setNewSyllabusDesc(e.target.value)}></textarea>
                    <button className="btn btn-primary" onClick={handleCreateSyllabus}>Create Course</button>
                  </div>
                  <div className="row">
                    {syllabi.map(syllabus => (
                      <div key={syllabus.id} className="col-md-4 mb-3">
                        <div className="card">
                          <div className="card-body">
                            {editingCourse && editingCourse.id === syllabus.id ? (
                              <div>
                                <input
                                  type="text"
                                  className="form-control mb-2"
                                  value={editTitle}
                                  onChange={(e) => setEditTitle(e.target.value)}
                                  placeholder="Course Title"
                                />
                                <textarea
                                  className="form-control mb-2"
                                  value={editDescription}
                                  onChange={(e) => setEditDescription(e.target.value)}
                                  placeholder="Course Description"
                                  rows="3"
                                />
                                <div className="d-flex gap-2">
                                  <button className="btn btn-success btn-sm" onClick={handleUpdateCourse}>Save</button>
                                  <button className="btn btn-secondary btn-sm" onClick={() => setEditingCourse(null)}>Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <h5>{syllabus.title}</h5>
                                <p>{syllabus.description}</p>
                                <div className="d-flex gap-2">
                                  <button className="btn btn-outline-primary btn-sm" onClick={() => handleEditCourse(syllabus)}>Edit</button>
                                  <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteCourse(syllabus.id)}>Delete</button>
                                  <button className="btn btn-outline-info btn-sm">Manage Topics</button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'videos' && (
              <div className="card">
                <div className="card-header">Video Management</div>
                <div className="card-body">
                  <div className="mb-4">
                    <div className="mb-3">
                      <select className="form-control" value={selectedVideo || ''} onChange={(e) => setSelectedVideo(e.target.value)}>
                        <option value="">Select Course</option>
                        {syllabi.map(course => (
                          <option key={course.id} value={course.id}>{course.title}</option>
                        ))}
                      </select>
                    </div>
                    <input type="text" className="form-control mb-2" placeholder="YouTube URL" value={url} onChange={(e) => setUrl(e.target.value)} />
                    <button className="btn btn-info mt-2 me-2" onClick={handlePreviewVideo}>Preview Video</button>
                    <button className="btn btn-primary mt-2" onClick={handleAddVideo} disabled={!selectedVideo}>Add Video</button>
                    {previewUrl && (
                      <div className="mt-3">
                        <iframe width="560" height="315" src={previewUrl} title="Video Preview" allowFullScreen></iframe>
                      </div>
                    )}
                  </div>
                  <div className="mb-4">
                    <h5>Add Question</h5>
                    <div className="mb-2">
                      <button className="btn btn-sm btn-outline-primary me-2" onClick={() => loadQuestionTemplate('mcq4')}>MCQ 4 Options</button>
                      <button className="btn btn-sm btn-outline-primary" onClick={() => loadQuestionTemplate('short')}>Short Answer</button>
                    </div>
                    <select className="form-control mb-2" value={questionType} onChange={(e) => setQuestionType(e.target.value)}>
                      <option value="multiple_choice">Multiple Choice</option>
                      <option value="short_answer">Short Answer</option>
                      <option value="essay">Essay</option>
                    </select>
                    <textarea className="form-control mb-2" placeholder="Question" value={questionText} onChange={(e) => setQuestionText(e.target.value)} />
                    {questionType === 'multiple_choice' && (
                      <div>
                        {options.map((opt, idx) => (
                          <input key={idx} type="text" className="form-control mb-1" placeholder={`Option ${idx + 1}`} value={opt} onChange={(e) => {
                            const newOpts = [...options];
                            newOpts[idx] = e.target.value;
                            setOptions(newOpts);
                          }} />
                        ))}
                      </div>
                    )}
                    <input type="text" className="form-control mb-2" placeholder="Correct Answer" value={answer} onChange={(e) => setAnswer(e.target.value)} />
                    <button className="btn btn-secondary" onClick={handleAddQuestion}>Add Question</button>
                  </div>
                  <div>
                    <h5>Videos</h5>
                    <div className="row">
                      {videos.map(video => (
                        <div key={video.id} className="col-md-4 mb-3">
                          <div className="card">
                            <div className="card-body">
                              <h6>{video.title || 'Untitled Video'}</h6>
                              <p>{video.url}</p>
                              <button className="btn btn-sm btn-info" onClick={() => fetchAnalytics(video.id)}>Analytics</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'homework' && (
              <div className="card">
                <div className="card-header">Homework Management</div>
                <div className="card-body">
                  <div className="mb-4">
                    <h5>Create New Homework</h5>
                    <div className="mb-3">
                      <select className="form-control" value={selectedVideo || ''} onChange={(e) => setSelectedVideo(e.target.value)}>
                        <option value="">Select Course</option>
                        {syllabi.map(course => (
                          <option key={course.id} value={course.id}>{course.title}</option>
                        ))}
                      </select>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <input type="text" className="form-control mb-2" placeholder="Homework Title" value={newHomeworkTitle} onChange={(e) => setNewHomeworkTitle(e.target.value)} />
                        <textarea className="form-control mb-2" placeholder="Description" rows="3"></textarea>
                        <input type="datetime-local" className="form-control mb-2" />
                      </div>
                      <div className="col-md-6">
                        <h6>Upload Study Materials (PDF)</h6>
                        <input type="file" className="form-control mb-2" accept=".pdf" />
                        <small className="text-muted">Upload PDF documents for students to study</small>
                      </div>
                    </div>
                    <button className="btn btn-primary mt-2" disabled={!selectedVideo}>Create Homework</button>
                  </div>
                  <div>
                    <h5>Existing Homework</h5>
                    <div className="row">
                      {homework.map(hw => (
                        <div key={hw.id} className="col-md-4 mb-3">
                          <div className="card">
                            <div className="card-body">
                              <h6>{hw.title}</h6>
                              <p>{hw.description}</p>
                              <p className="text-muted">Due: {hw.due_date}</p>
                              <div className="d-flex gap-2">
                                <button className="btn btn-sm btn-outline-secondary">View Submissions</button>
                                <button className="btn btn-sm btn-outline-info">Download Materials</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'analytics' && (
              <div className="card">
                <div className="card-header">Student Progress Analytics</div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-3">
                      <div className="card text-center">
                        <div className="card-body">
                          <h3>85%</h3>
                          <p>Average Completion</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card text-center">
                        <div className="card-body">
                          <h3>92%</h3>
                          <p>Average Score</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card text-center">
                        <div className="card-body">
                          <h3>45</h3>
                          <p>Total Students</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card text-center">
                        <div className="card-body">
                          <h3>12</h3>
                          <p>Active Courses</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Teacher;